import { useParams, useNavigate } from 'react-router-dom'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  AvailableBalanceCard,
  FlaggedTransactionsCard,
  CurrentMonthlyVolumesCard,
  LiquidityProviderCard,
  SearchInput,
  TabsContainer,
  LoadingPage,
  GroupStatusBadge,
  ButtonVariant,
  BPSBadge,
  StatsWrapper,
  FallbackGroupIcon,
} from '@valiant/ui-shared'
import { useGetGroupQuery, useGetGroupStatsQuery, type UserRole } from '@/generated/graphql'
import { CompaniesTab } from '../components/tabs/CompaniesTab'
import { UsersTab } from '../components/tabs/UsersTab'
import { TransactionsTab } from '../components/tabs/TransactionsTab'
import { VolumesTab } from '../components/tabs/VolumesTab'
import { useQueryParams } from '@valiant/ui-shared'
import { useEffect, useMemo, useState } from 'react'
import { MainLayout } from '@/components/layout/MainLayout'
import { Settings, Network } from 'lucide-react'

export function Group() {
  const [searchTerm, setSearchTerm] = useState('')
  const { groupId } = useParams<{ groupId: string }>()
  const navigate = useNavigate()
  const queryParams = useQueryParams('companies')
  const [{ data: groupsData, fetching: groupsLoading, error: groupsError }] = useGetGroupQuery({
    variables: { groupId: groupId || '' },
    pause: !groupId,
  })
  const [{ data: groupStatsData, fetching: groupStatsLoading, error: groupStatsError }] =
    useGetGroupStatsQuery({
      variables: { groupId: groupId! },
      pause: !groupId,
    })

  const group = groupsData?.getGroup
  const groupStats = groupStatsData?.getGroupStats
  const isLoading = groupsLoading || groupStatsLoading

  useEffect(() => {
    setSearchTerm('')
  }, [queryParams.tab])

  const badges = useMemo(() => {
    if (!group) return []
    return [
      {
        node: <GroupStatusBadge status={group.status} />,
        direction: 'LEFT',
      },
      {
        node: <BPSBadge bps={group.bps} tone="group" />,
        direction: 'RIGHT',
      },
    ]
  }, [group])

  const buttons = useMemo(() => {
    if (!group) return []
    return [
      {
        label: 'View Org Graph',
        variant: ButtonVariant.SECONDARY,
        icon: <Network className="h-3 w-3 md:h-4 md:w-4" />,
        onClick: () => navigate(`/org-graph?groupId=${groupId}`),
      },
      {
        label: 'Manage',
        variant: ButtonVariant.MAIN_ACTION,
        icon: <Settings className="h-3 w-3 md:h-4 md:w-4" />,
        onClick: () => navigate(`/groups/${groupId}/manage`),
      },
    ]
  }, [groupId, navigate, group])

  if (isLoading) {
    return <LoadingPage />
  }

  if (groupsError || !group) {
    return (
      <MainLayout>
        <div>Group not found</div>
      </MainLayout>
    )
  }

  if (groupStatsError) {
    console.error('Group stats error:', groupStatsError)
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: group.name,
          imageUrl: group.imageUrl || undefined,
          fallbackImage: <FallbackGroupIcon />,
        },
      ]}
      headerTitle={group.name}
      headerImage={group.imageUrl || <FallbackGroupIcon />}
      badges={badges}
      buttons={buttons}
    >
      {/* Stats Cards */}
      <StatsWrapper>
        <LiquidityProviderCard
          liquidityProvider={groupStats?.liquidityProvider ?? '-'}
          bps={groupStats?.liquidityProviderBps ?? 0}
          valiantBps={groupStats?.valiantBps ?? undefined}
          onClick={() => {}}
        />

        <AvailableBalanceCard
          availableBalance={groupStats?.availableBalance ?? '-'}
          loading={groupStatsLoading}
          onClick={() => {
            queryParams.updateMultiple({
              tab: 'companies',
              status: undefined,
              hasFunds: 'true',
            })
          }}
        />
        <FlaggedTransactionsCard
          flaggedTransactions={groupStats?.flaggedTransactions ?? 0}
          onClick={() => {
            queryParams.updateMultiple({
              tab: 'transactions',
              isFlagged: 'true',
              status: undefined,
              type: undefined,
            })
          }}
        />
        <CurrentMonthlyVolumesCard
          currentMonthlyVolumes={groupStats?.currentMonthlyVolumes ?? '-'}
          previousMonthlyVolumes={groupStats?.previousMonthlyVolumes ?? '-'}
          onClick={() => {
            queryParams.updateMultiple({ tab: 'volumes', status: undefined })
          }}
        />
      </StatsWrapper>

      {/* Tabs Section */}
      <Tabs
        value={queryParams.tab || 'companies'}
        onValueChange={(value) => {
          // Clear transactions filter when switching to other tabs
          if (value !== 'transactions') {
            queryParams.updateMultiple({
              tab: value,
              status: undefined,
              type: undefined,
              isFlagged: undefined,
              hasFunds: value === 'companies' ? queryParams.hasFunds : undefined,
            })
          } else {
            queryParams.updateTab(value)
          }
        }}
        className="space-y-4"
      >
        <TabsContainer>
          <TabsList>
            <TabsTrigger value="companies">Companies</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="volumes">Volumes</TabsTrigger>
          </TabsList>

          <SearchInput
            placeholder="Search"
            className="w-[200px]"
            value={searchTerm}
            onChange={setSearchTerm}
          />
        </TabsContainer>

        <TabsContent value="companies" className="space-y-4">
          <CompaniesTab
            groupId={group.groupId}
            searchTerm={searchTerm}
            preSelectedGroup={{
              groupId: group.groupId,
              name: group.name,
              status: group.status,
            }}
          />
        </TabsContent>

        <TabsContent value="users">
          <UsersTab
            groupId={group.groupId}
            showUnassignUserFromGroup={true}
            searchTerm={searchTerm}
            adminOnly={false}
            availableRoleFilters={['TRADER', 'GROUP_OWNER']}
            allowedRoles={['GROUP_OWNER', 'TRADER'] as UserRole[]}
          />
        </TabsContent>

        <TabsContent value="transactions">
          <TransactionsTab groupId={group.groupId} searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="volumes">
          <VolumesTab groupId={group.groupId} searchTerm={searchTerm} />
        </TabsContent>
      </Tabs>
    </MainLayout>
  )
}
